import type { SubscriptionCost, SubscriptionResponse } from './types'

/**
 * How many users the account may have active: what the plan includes, plus what has been bought.
 *
 * **This is the only arithmetic this console does.** Every money figure arrives from the API already
 * worked out in exact cents, because JavaScript has no exact decimal type and these are amounts a
 * customer reads. A count of people is not an amount, and two integers added here save a round trip
 * for a number the page already holds both halves of.
 *
 * `null` means unlimited and is returned untouched. Coercing it to 0 would refuse every hire a
 * legacy account makes; adding to it would claim an unlimited plan covers exactly what it bought.
 * A `maxUsers` of 0 is a real, provisionable limit rather than "unset", so it is added to normally.
 */
export function seatCapacity(
  subscription: Pick<SubscriptionResponse, 'maxUsers' | 'purchasedSeats'>,
): number | null {
  if (subscription.maxUsers === null) return null
  return subscription.maxUsers + subscription.purchasedSeats
}

/** The per-user rate a bought seat is charged at today, and whether that is the offer rate. */
export interface SeatRate {
  /** Whole cents, per bought user, per month. */
  cents: number
  /** True when this is the introductory rate rather than the standing one. */
  introductory: boolean
}

/**
 * Which of the two published per-user rates a bought seat is actually charged at.
 *
 * Both arrive from the API; this picks one. `introEndsAt` is null until the account's first payment
 * succeeds, and null means the window has NOT STARTED rather than that it is spent — a trialling
 * account has all three months ahead of it, so the rate it is agreeing to is the introductory one.
 * A date in the past is the only state in which the standing rate applies.
 *
 * `now` is a parameter rather than read here so the choice is testable and so one render cannot
 * disagree with itself halfway down the page.
 */
export function seatRateInForce(
  cost: Pick<SubscriptionCost, 'seatCents' | 'introSeatCents' | 'introEndsAt'>,
  now: Date,
): SeatRate {
  const over = cost.introEndsAt !== null && new Date(cost.introEndsAt).getTime() <= now.getTime()
  return over
    ? { cents: cost.seatCents, introductory: false }
    : { cents: cost.introSeatCents, introductory: true }
}

/**
 * What a given number of bought users comes to each month, at the rate in force.
 *
 * **The only money this console works out rather than reads**, and it earns the exception three
 * times over: a customer about to buy has to be shown what it costs before they agree, a customer
 * giving capacity back has to be shown what it saves, and the Extra users card shows what the seats
 * already held cost at the rate IN FORCE — which inside the introductory window is not
 * `cost.seatsCents`, because the API publishes no `introSeatsCents` to read instead.
 *
 * It is exact — whole cents times a small integer — and it mirrors the API's own formula, which
 * `SubscriptionCost.seatsCents` proves out on every reload once the window has passed.
 *
 * Deliberately NOT a new monthly total. Bought users are billed monthly on both intervals while an
 * annual plan fee is not, so plan-plus-users is a blended figure on an annual account and quoting it
 * as "your new monthly bill" would overstate what is actually taken each month. This figure is true
 * on either interval.
 */
export function monthlySeatCostCents(
  cost: Pick<SubscriptionCost, 'seatCents' | 'introSeatCents' | 'introEndsAt'>,
  seats: number,
  now: Date,
): number {
  return seatRateInForce(cost, now).cents * Math.max(0, Math.trunc(seats))
}

/**
 * The subscription as it may arrive from an API build that predates bought capacity: everything
 * `SubscriptionResponse` has, with the two seat fields possibly missing.
 */
type SubscriptionWire = Omit<SubscriptionResponse, 'purchasedSeats' | 'seatsReleaseAtPeriodEnd'> &
  Partial<Pick<SubscriptionResponse, 'purchasedSeats' | 'seatsReleaseAtPeriodEnd'>>

/**
 * Fills in the two seat fields, so that every reader after this point can trust the type.
 *
 * They are declared as always present and the whole page relies on it. If they are not, three
 * things go wrong at once and all three are customer-visible: `seatCapacity` answers `NaN` and the
 * meter draws "1 of NaN"; the Extra users card formats `NaN` as money and prints "$NaN.NaN" on a
 * billing page; and `seatsReleaseAtPeriodEnd !== null` is TRUE for `undefined`, so the account is
 * told it has scheduled a release it never asked for, and both buttons disappear behind it.
 *
 * The console and the API deploy as separate images, so the console can be live for a few minutes
 * against a backend that has not caught up. This is what makes those minutes read as "nothing
 * bought" rather than as nonsense.
 *
 * `??`, not `||`: a pending release of 0 is a customer giving back everything they had bought, and
 * `|| null` would turn that into "nothing is scheduled" — after which the capacity would look as
 * though it were staying for ever.
 */
export function normaliseSeatFields(subscription: SubscriptionWire): SubscriptionResponse {
  return {
    ...subscription,
    purchasedSeats: subscription.purchasedSeats ?? 0,
    seatsReleaseAtPeriodEnd: subscription.seatsReleaseAtPeriodEnd ?? null,
  }
}

/** A failed extra-user invoice, as the billing page needs to speak about it. */
export interface SeatDunning {
  /**
   * Whether new shift assignments are being refused RIGHT NOW, as against a deadline still ahead.
   * The difference is the whole message: one is "pay by Monday", the other is "this is happening".
   */
  restricted: boolean
  /** ISO 8601: when the invoice first failed. */
  since: string
  /** ISO 8601: when assignments stop, or null when the API did not say. */
  from: string | null
}

/**
 * Whether this account's extra users are unpaid, and whether that is already biting.
 *
 * Returns null for the normal state — paid, or nothing bought — so the caller renders nothing at
 * all rather than a card explaining that everything is fine.
 *
 * The clock decides between the two live states, and it is the API's date that it compares against,
 * never a locally computed one: the grace period belongs to the backend's `SeatDunningRules`, and a
 * console that hard-coded "seven days" would disagree with the product the day somebody changed it.
 *
 * `>=`, matching the backend's own `IsRestrictedAt`: the instant the window opens is already
 * restricted. Reading it as "not yet" would promise a day of grace the product does not give, and
 * the customer would find out from a dispatcher who cannot roster.
 *
 * A missing `seatsRestrictedFrom` alongside a real `seatPastDueSince` is reported as unpaid with no
 * date rather than as paid. It should not happen — the API derives one from the other — but if it
 * does, a dispatcher is already being refused, and silence would leave nobody able to explain why.
 */
export function seatDunning(
  subscription: Pick<SubscriptionResponse, 'seatPastDueSince' | 'seatsRestrictedFrom'>,
  now: Date,
): SeatDunning | null {
  if (subscription.seatPastDueSince === null) return null

  const from = subscription.seatsRestrictedFrom
  return {
    restricted: from !== null && new Date(from).getTime() <= now.getTime(),
    since: subscription.seatPastDueSince,
    from,
  }
}
